import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RegenerateModal } from "@/components/ai-assistant/RegenerateModal";
import MainLayout from "@/components/layout/MainLayout";
import PageHeader from "@/components/layout/PageHeader";
import { useAuth } from "@/contexts/AuthContext";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useInfiniteQuery,
} from "@tanstack/react-query";
import { aiAssistantService } from "@/services/aiAssistantService";
import { Tables } from "@/types/database.types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
//
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Star,
  Loader2,
  Search,
  Trash2,
  Calendar,
  User as UserIcon,
  ArrowLeft,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { OutputHeader } from "@/components/ai-assistant/OutputHeader";
import { AIOutputView } from "@/components/ai-assistant/AIOutputView";
import { CopyContentButton } from "@/components/ai-assistant/CopyContentButton";
import { FolderAssignDropdown } from "@/components/ai-assistant/FolderAssignDropdown";
import { ShareOutputDialog } from "@/components/ai-assistant/ShareOutputDialog";
import { profileService } from "@/services/profileService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type AIOutput = Tables<"ai_outputs">;

export default function LibraryPage() {
  const { organization } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [resultsFilter, setResultsFilter] = useState<
    "saved" | "all" | "external"
  >("saved");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedAssistantId, setSelectedAssistantId] = useState<
    string | "all"
  >("all");
  const [selectedOutput, setSelectedOutput] = useState<
    (AIOutput & { ai_assistant?: any; ai_assistant_instance?: any }) | null
  >(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [isImproving, setIsImproving] = useState(false);
  const [showMobileDetailView, setShowMobileDetailView] = useState(false);

  const { data: systemAssistants } = useQuery({
    queryKey: ["systemAssistants"],
    queryFn: async () => {
      const res = await aiAssistantService.getSystemAssistants();
      if (res.error) throw res.error;
      return res.data || [];
    },
  });

  const {
    data: orgOutputs,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      "organizationOutputs",
      organization?.id,
      resultsFilter,
      selectedAssistantId,
      searchTerm,
    ],
    queryFn: async ({ pageParam = 0 }) => {
      if (!organization?.id) return { data: [], hasMore: false };

      const filters = {
        onlySaved: resultsFilter === "saved",
        onlyExternal: resultsFilter === "external",
        assistantId:
          selectedAssistantId !== "all" ? selectedAssistantId : undefined,
        searchTerm: searchTerm.trim() || undefined,
      };

      const res = await aiAssistantService.getOrganizationOutputs(
        organization.id,
        30,
        pageParam * 30,
        filters,
      );
      if (res.error) throw res.error;

      let data = res.data || [];
      // Enrich with user names for consistent display (date + user)
      const userIds = Array.from(
        new Set(data.map((o: any) => o.user_id).filter(Boolean)),
      );
      if (userIds.length > 0) {
        const prof = await profileService.getProfilesByIds(userIds as string[]);
        if (prof.success && prof.data) {
          const idToName = new Map<string, string>();
          prof.data.forEach((p) => {
            const full =
              p.fullName || [p.firstName, p.lastName].filter(Boolean).join(" ");
            if (p.id && full) idToName.set(p.id, full);
          });
          data = data.map((o: any) => ({
            ...o,
            user_name: o.user_id ? idToName.get(o.user_id) || "" : "",
          }));
        }
      }
      return {
        data,
        hasMore: data.length === 30,
        nextPage: data.length === 30 ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!organization?.id,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });

  // Flatten all pages into a single array
  const allOutputs = React.useMemo(() => {
    return orgOutputs?.pages?.flatMap((page) => page.data) || [];
  }, [orgOutputs]);

  // No need for client-side filtering since it's done server-side
  const filteredOutputs = allOutputs;

  const isOutputSaved = (id: string) =>
    allOutputs.some((o) => o.id === id && o.is_saved);

  const saveMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await aiAssistantService.saveAIOutput(id);
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      // Refresh all organization outputs queries
      queryClient.invalidateQueries({
        queryKey: ["organizationOutputs", organization?.id],
      });
      toast({ title: "Starred", description: "Output added to Starred." });
    },
    onError: (e: any) =>
      toast({
        title: "Star failed",
        description: e?.message || "Error",
        variant: "destructive",
      }),
  });

  const unsaveMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await aiAssistantService.deleteSavedOutput(id);
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      // Refresh all organization outputs queries
      queryClient.invalidateQueries({
        queryKey: ["organizationOutputs", organization?.id],
      });
      toast({ title: "Removed", description: "Removed from saved." });
    },
    onError: (e: any) =>
      toast({
        title: "Remove failed",
        description: e?.message || "Error",
        variant: "destructive",
      }),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await aiAssistantService.deleteAIOutput(id);
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: (_, id) => {
      // Refresh all organization outputs queries
      queryClient.invalidateQueries({
        queryKey: ["organizationOutputs", organization?.id],
      });
      if (selectedOutput?.id === id) setSelectedOutput(null);
      toast({ title: "Deleted", description: "Generation deleted." });
    },
    onError: (e: any) =>
      toast({
        title: "Delete failed",
        description: e?.message || "Error",
        variant: "destructive",
      }),
  });

  const assistantsForCombo = useMemo(() => {
    const list = systemAssistants || [];

    // Separate AI Tools (generators) and AI Assistants (chat)
    const aiTools = list.filter((a: any) => !a.supports_chat);
    const aiAssistants = list.filter((a: any) => a.supports_chat);

    const options = [{ id: "all", title: "All Tools & Assistants" }];

    // Add AI Tools section
    if (aiTools.length > 0) {
      options.push({
        id: "ai-tools-header",
        title: "─── AI Tools ───",
        disabled: true,
      });
      options.push(...aiTools.map((a: any) => ({ id: a.id, title: a.title })));
    }

    // Add AI Assistants section
    if (aiAssistants.length > 0) {
      options.push({
        id: "ai-assistants-header",
        title: "─── AI Assistants ───",
        disabled: true,
      });
      options.push(
        ...aiAssistants.map((a: any) => ({ id: a.id, title: a.title })),
      );
    }

    return options;
  }, [systemAssistants]);

  const renderingSchema = useMemo(() => {
    const a = (selectedOutput as any)?.ai_assistant;
    const inst = (selectedOutput as any)?.ai_assistant_instance;
    return inst?.custom_zod_schema || a?.zod_schema || null;
  }, [selectedOutput]);

  // Create form fields from input_data keys for display
  const getFormFieldsFromInputData = (inputData: any) => {
    if (!inputData || typeof inputData !== "object") return [];
    return Object.keys(inputData).map((key) => ({ label: key }));
  };

  return (
    <MainLayout>
      <PageHeader title="History" />
      <div className="flex-1 overflow-hidden">
        <div className="flex gap-4 h-full p-4">
          {/* Left Panel - List View (hidden on mobile when detail is shown) */}
          <div
            className={`w-full md:w-[380px] bg-white border rounded-lg overflow-hidden flex flex-col ${
              showMobileDetailView ? "hidden md:flex" : "flex"
            }`}
          >
            <div className="p-3 border-b space-y-3">
              {/* Assistant select */}
              <div>
                <Select
                  value={String(selectedAssistantId)}
                  onValueChange={(v) => setSelectedAssistantId(v as any)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Tools & Assistants" />
                  </SelectTrigger>
                  <SelectContent>
                    {assistantsForCombo.map((a) => (
                      <SelectItem
                        key={a.id}
                        value={String(a.id)}
                        disabled={(a as any).disabled}
                        className={
                          (a as any).disabled
                            ? "text-gray-400 font-medium text-center cursor-default"
                            : ""
                        }
                      >
                        {a.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Tabs */}
              <div>
                <Tabs
                  value={resultsFilter}
                  onValueChange={(v) => setResultsFilter(v as any)}
                >
                  <TabsList>
                    <TabsTrigger value="saved">Starred</TabsTrigger>
                    <TabsTrigger value="all">All</TabsTrigger>
                    <TabsTrigger value="external">External</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  className="pl-8"
                  placeholder="Search outputs"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              {/* Count */}
              <div className="text-xs text-gray-500">
                {isLoading ? "Loading..." : `${filteredOutputs.length} results`}
              </div>
            </div>
            <div className="flex-1 overflow-auto divide-y">
              {filteredOutputs.length > 0 ? (
                filteredOutputs.map((output) => (
                  <div
                    key={output.id}
                    className={`p-3 cursor-pointer ${
                      selectedOutput?.id === output.id
                        ? "bg-gray-50"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      setSelectedOutput(output);
                      setShowMobileDetailView(true);
                    }}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {(output as any).output_title || "Generated Output"}
                        </div>
                        <div className="text-xs text-gray-500 truncate flex items-center gap-3">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(output.created_at).toLocaleDateString(
                              undefined,
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              },
                            )}
                          </span>
                          {!!(output as any).user_id && (
                            <span className="inline-flex items-center gap-1">
                              <UserIcon className="h-3 w-3" />
                              {(output as any).user_name || "User"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isOutputSaved(output.id)) {
                              unsaveMutation.mutate(output.id);
                            } else {
                              saveMutation.mutate(output.id);
                            }
                          }}
                          className="h-6 w-6 p-0"
                          title={isOutputSaved(output.id) ? "Unstar" : "Star"}
                        >
                          <Star
                            className={`h-3 w-3 ${
                              isOutputSaved(output.id) ? "text-yellow-500" : ""
                            }`}
                            fill={
                              isOutputSaved(output.id) ? "currentColor" : "none"
                            }
                          />
                        </Button>
                        <Button
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            const confirmed = window.confirm(
                              "Are you sure you want to delete this output?",
                            );
                            if (confirmed) deleteMutation.mutate(output.id);
                          }}
                          className="h-6 w-6 p-0 hover:bg-red-50 hover:text-red-600"
                        >
                          {deleteMutation.isPending ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-sm text-gray-500">
                  No results found
                </div>
              )}

              {/* Load More Button */}
              {hasNextPage && !isLoading && filteredOutputs.length > 0 && (
                <div className="p-4 border-t">
                  <Button
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    variant="outline"
                    className="w-full"
                  >
                    {isFetchingNextPage ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      "Load More"
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
          {/* Right Panel - Detail View (full width on mobile when shown) */}
          <div
            className={`flex-1 bg-gray-50 rounded-lg border overflow-auto ${
              showMobileDetailView
                ? "fixed inset-0 z-50 md:relative md:inset-auto md:z-auto"
                : "hidden md:block"
            }`}
          >
            {/* Mobile back button */}
            {showMobileDetailView && (
              <div className="md:hidden sticky top-0 z-10 bg-white border-b p-4">
                <Button
                  variant="ghost"
                  onClick={() => setShowMobileDetailView(false)}
                  className="flex items-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to History
                </Button>
              </div>
            )}

            <div className="p-6">
              <div className="max-w-[750px] mx-auto">
                {selectedOutput ? (
                  <AIOutputView
                    key={(selectedOutput as any)?.id}
                    output={selectedOutput}
                    isSaved={isOutputSaved(selectedOutput.id)}
                    onToggleSave={() => {
                      if (isOutputSaved(selectedOutput.id))
                        unsaveMutation.mutate(selectedOutput.id);
                      else saveMutation.mutate(selectedOutput.id);
                    }}
                    renderingSchema={renderingSchema as any}
                    isGenerating={false}
                    onImprove={() => setShowRegenerateModal(true)}
                    onTitleSave={async (newTitle) => {
                      await aiAssistantService.updateAIOutputTitle(
                        (selectedOutput as any).id,
                        newTitle,
                      );
                      setSelectedOutput((prev) =>
                        prev ? { ...prev, output_title: newTitle } : prev,
                      );
                    }}
                    extraActionsRight={
                      <FolderAssignDropdown
                        outputId={(selectedOutput as any).id}
                        currentFolderId={
                          (selectedOutput as any).folder_id || null
                        }
                        onChange={(folderId) => {
                          setSelectedOutput((prev) =>
                            prev
                              ? { ...prev, folder_id: folderId as any }
                              : prev,
                          );
                        }}
                      />
                    }
                  />
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="h-6 w-6 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Select a Result
                    </h3>
                    <p className="text-gray-500">
                      Choose a generation from the left to view its content.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <RegenerateModal
          isOpen={showRegenerateModal}
          onClose={() => setShowRegenerateModal(false)}
          onRegenerate={async (fb?: string) => {
            if (!selectedOutput) return;
            try {
              setIsImproving(true);
              const organizationId =
                organization?.id || (selectedOutput as any).organization_id;
              const assistantId = (selectedOutput as any)
                .ai_assistant_id as string;
              const instanceId = (selectedOutput as any)
                .ai_assistant_instance_id as string | null;
              // Reuse assistant config via a lightweight fetch of instance/base if needed
              const base =
                await aiAssistantService.getSystemAssistant(assistantId);
              if (base.error || !base.data)
                throw base.error || new Error("Assistant not found");
              const assistantConfig: any = {
                baseInstructions: (base.data as any).base_instructions,
                customInstructions: "",
                zodSchema: (base.data as any).zod_schema,
                formFields: (base.data as any).form_fields,
                model: (base.data as any).model,
                temperature: (base.data as any).temperature,
                max_output_tokens: (base.data as any).max_output_tokens,
                web_search_enabled: (base.data as any).web_search_enabled,
                generation_mode: (base.data as any).generation_mode || "object",
              };
              const url = `${(window as any).ENV?.VITE_SUPABASE_URL || (import.meta as any).env?.VITE_SUPABASE_URL}/functions/v1/generate-with-assistant`;
              const reqBody: any = {
                assistantId,
                instanceId,
                inputData: (selectedOutput as any).input_data || {},
                organizationId,
                userId: (window as any).ENV?.USER_ID || undefined,
                assistantConfig,
                isRegeneration: true,
                previousOutput: (selectedOutput as any).structured_output,
                userFeedback: fb,
                outputId: (selectedOutput as any).id,
              };
              const resp = await fetch(url, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${(window as any).ENV?.VITE_SUPABASE_ANON_KEY || (import.meta as any).env?.VITE_SUPABASE_ANON_KEY}`,
                },
                body: JSON.stringify(reqBody),
              });
              const reader = resp.body?.getReader();
              if (!reader) throw new Error("Failed to open stream");
              const decoder = new TextDecoder();
              let buffer = "";
              let textBuffer = "";
              // wipe content first
              setSelectedOutput((prev: any) =>
                prev ? { ...prev, structured_output: "" } : prev,
              );
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n\n");
                buffer = lines.pop() || "";
                for (const line of lines) {
                  if (!line.startsWith("data: ")) continue;
                  try {
                    const data = JSON.parse(line.slice(6));
                    if (data.type === "text-delta") {
                      const delta: string = data.data || "";
                      textBuffer += delta;
                      setSelectedOutput((prev: any) =>
                        prev
                          ? { ...prev, structured_output: textBuffer }
                          : prev,
                      );
                    }
                    if (data.type === "partial") {
                      const part = data.data;
                      setSelectedOutput((prev: any) =>
                        prev ? { ...prev, structured_output: part } : prev,
                      );
                    }
                    if (data.type === "complete") {
                      const finalData = data.data;
                      setSelectedOutput((prev: any) =>
                        prev ? { ...prev, structured_output: finalData } : prev,
                      );
                    }
                  } catch (_) {}
                }
              }
            } catch (e) {
              // ignore for now
            } finally {
              setIsImproving(false);
            }
          }}
          isGenerating={isImproving}
        />
      </div>
    </MainLayout>
  );
}
